import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { DialogPensionDivisionOfRulings } from 'app/dialogs/pension/division-of-rulings/dialog-pension-division-of-rulings';
import { TranslocoLocaleWrapperService } from 'app/fuse-template/core/transloco/translocoLocale.service';
import { DropDownItem } from 'app/interfaces/items/dropDownItem';
import { ExposedListDivisionOfRulingsLSRARes } from 'app/interfaces/responses/Exposed/exposedListDivisionOfRulingsLSRARes';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { PensionService } from 'app/services/api/pension/pension.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { SORTING } from 'common/constants/config.constants';
import { catchError, map, of as observableOf, startWith, Subscription, switchMap } from 'rxjs';

@Component({
    selector: 'app-pension-division-of-rulings.component',
    templateUrl: './pension-division-of-rulings.component.html',
    styleUrls: ['./pension-division-of-rulings.component.scss']
})
export class PensionDivisionOfRulingsComponent implements OnInit {
    //#region Globals

    //Table
    pageSize: number = 50;
    pageNumber: number = 0;
    pageSizes = [10, 25, 50, 100, 200];
    paginatorSubscription!: Subscription;
    dataSource = new MatTableDataSource<ExposedListDivisionOfRulingsLSRARes>();
    divisionOfRulingsData: ExposedListDivisionOfRulingsLSRARes[];
    totalData: number = 0;
    tableColumns: string[] = [];
    sorting = SORTING.SORTING_DESC;
    @ViewChild("paginator") paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    // Form
    form: UntypedFormGroup;
    rulingStatusDropDown: Array<DropDownItem> = [];
    ratioDropDown: Array<DropDownItem> = [];
    paymentTransactionTypeDropDown: Array<DropDownItem> = [];
    isLoading = true;

    //#endregion

    //#region Init
    constructor(
        private formBuilder: UntypedFormBuilder,
        public pensionService: PensionService,
        public lsrauthService: LsrauthService,
        public translationService: TranslationService,
        private localeService: TranslocoLocaleWrapperService,
        public dialog: MatDialog
    ) { }

    ngOnInit(): void {
        this.initForm();
        this.initDropdowns();
        this.initTableColumns();
    }

    ngAfterViewInit() {
        this.fetchData();
    }

    initForm(): void {
        this.form = this.formBuilder.group({
            searchString: new UntypedFormControl(''),
            dateFrom: new UntypedFormControl(),
            dateTo: new UntypedFormControl(),
            divisionOfRulingStatus: new UntypedFormControl(''),
            ratio: new UntypedFormControl(''),
            paymentTransactionType: new UntypedFormControl(''),
        });
    }

    initDropdowns(): void {
        this.pensionService.getDivisionOfRulingsConfigs().subscribe((res) => {
            res.divisionOFRulingsRatio.forEach(x => {
                this.ratioDropDown.push({
                    value: x.ratioName,
                    viewValue: x.ratioName
                });
            });
            res.divisionOFRulingsStatus.forEach(x => {
                this.rulingStatusDropDown.push({
                    value: x.statusName,
                    viewValue: x.statusName
                });
            });
            res.kriaPaymentTransactionType.forEach(x => {
                this.paymentTransactionTypeDropDown.push({
                    value: x.pensionTypeName,
                    viewValue: this.translationService.translate('label_pension_type_' + x.pensionTypeName)
                });
            });
        });
    }

    fetchData(): any {
        if (this.paginatorSubscription) {
            this.paginatorSubscription.unsubscribe();
        }

        this.pageNumber = 0;
        this.dataSource.paginator = this.paginator;
        this.paginator.pageIndex = 0;

        this.paginatorSubscription = this.paginator.page
            .pipe(
                startWith({}),
                switchMap(() => {
                    this.isLoading = true;
                    return this.fetchRulings(
                        this.paginator.pageIndex,
                        this.paginator.pageSize,
                        this.sorting,
                        true
                    ).pipe(catchError(() => observableOf(null)));
                }),
                map((divisionOfRulingsData) => {
                    this.isLoading = false;
                    return divisionOfRulingsData ?? [];
                })
            )
            .subscribe((divisionOfRulingsData) => {
                this.divisionOfRulingsData = divisionOfRulingsData;
                if (divisionOfRulingsData.length > 0) {
                    this.totalData = divisionOfRulingsData[0].totalNumberOfEntries;
                    this.paginator.length = divisionOfRulingsData[0].totalNumberOfEntries;
                } else {
                    this.totalData = 0;
                }
                this.dataSource = new MatTableDataSource(divisionOfRulingsData);
            });
    }

    fetchRulings(pageNumber: number, pageSize: number, sorting: string = this.sorting, scrollToTop = false) {
        return this.pensionService.getDivisionOfRulingsLSRA({
            searchId: this.form.get('searchString').value,
            dateFrom: this.form.get('dateFrom').value,
            dateTo: this.form.get('dateTo').value,
            divisionOfRulingStatus: this.form.get('divisionOfRulingStatus').value,
            divisionOfRulingRatio: this.form.get('ratio').value,
            paymentTransactionType: this.form.get('paymentTransactionType').value,
            pageNumber: pageNumber,
            pageSize: pageSize,
            sorting: sorting
        });
    }

    //#endregion

    //#region Table functions

    initTableColumns(): any {
        this.tableColumns = ['rulingNumber', 'paymentTransactionTypeAcronym', 'receiverSSN', 'atRatio', 'jaRatio', 'divisionOfRulingStatus', 'divisionOfRulingChanged'];
    }

    pageChanged(event: PageEvent): any {
        this.pageNumber = event.pageIndex;
        this.pageSize = event.pageSize;
        this.fetchRulings(this.pageNumber, this.pageSize, this.sorting, true);
    }

    sortData(sort: Sort) {
        this.pageNumber = 0;
        if (this.sorting == SORTING.SORTING_DESC) {
            this.sorting = SORTING.SORTING_ASC;
        }
        else {
            this.sorting = SORTING.SORTING_DESC;
        }
        this.fetchData();
    }


    //#endregion

    //#region dialog functions

    viewDivisionOfRulingDetails(ruling: ExposedListDivisionOfRulingsLSRARes) {
        const dialogRef = this.dialog.open(DialogPensionDivisionOfRulings, {
            autoFocus: false,
            restoreFocus: false,
            width: '1350px',
            data: {
                ruling: ruling,
            }
        });

        dialogRef.afterClosed().subscribe(result => {

        });

        // Changes performed in the dialog for the selected ruling are updated in the overview list.
        dialogRef.componentInstance.updateListDivisionOfRulingsLSRARes
            .subscribe(data => {
                const index = this.divisionOfRulingsData.findIndex(x => x.rulingNumber == data.rulingNumber);
                if (index != -1) {
                    this.divisionOfRulingsData[index] = data;
                }
                const currentRulingDetails = this.divisionOfRulingsData.find(x => x.rulingNumber == data.rulingNumber)
            });
    }

    //#endregion
}
